/**
 * Local wall-clock helpers for the delivery date/time draft.
 *
 * The draft's `deliveryDate` is either an ISO instant (`...Z`, what a fresh
 * draft starts with) or the naive local string this picker composes
 * (`YYYY-MM-DDTHH:mm:00`). The server treats both the same way
 * (`toTenantWallClock`), so the ONLY rule here is: read and write the
 * cashier's LOCAL clock, never UTC.
 *
 * The previous helpers sliced `toISOString()` — UTC — to fill the time input
 * and then composed the picked value back as a naive LOCAL string. Every edit
 * shifted the shown time by the tenant's UTC offset (05:53 picked in Kuwait
 * displayed as 02:53), which is the "time picker selects the wrong value" bug.
 */

const NAIVE_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Parse the draft value into a local Date. Invalid input → now. */
export function parseDraftDate(value: string | null | undefined): Date {
  const s = (value ?? '').trim();
  if (!s) return new Date();
  const m = NAIVE_LOCAL_RE.exec(s);
  if (m) {
    // Component-wise so a naive string is read as LOCAL on every engine.
    return new Date(
      Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4]), Number(m[5]), Number(m[6] ?? '0'), 0,
    );
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** `YYYY-MM-DD` of the LOCAL calendar day — for `<input type="date">`. */
export function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Naive LOCAL `YYYY-MM-DDTHH:mm:00`, what the draft stores and the server takes. */
export function toDraftValue(d: Date): string {
  return `${toDateInputValue(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
}

/** Twelve-hour split of a local Date. */
export function splitTwelveHour(d: Date): { hour12: number; minute: number; pm: boolean } {
  const h = d.getHours();
  return { hour12: h % 12 === 0 ? 12 : h % 12, minute: d.getMinutes(), pm: h >= 12 };
}

export function toHour24(hour12: number, pm: boolean): number {
  const base = hour12 % 12; // 12 → 0
  return pm ? base + 12 : base;
}

/** Replace the calendar day, keeping the time. `date` is `YYYY-MM-DD`. */
export function withDate(d: Date, date: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return d;
  const next = new Date(d.getTime());
  next.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return next;
}

/** Replace the time of day, keeping the calendar day. */
export function withTime(d: Date, hour24: number, minute: number): Date {
  const next = new Date(d.getTime());
  next.setHours(hour24, minute, 0, 0);
  return next;
}

/** Now plus `minutes`, rounded UP to the next 5-minute mark so it lands on a picker option. */
export function fromNow(minutes: number, now: Date = new Date()): Date {
  const d = new Date(now.getTime() + minutes * 60_000);
  const rem = d.getMinutes() % 5;
  if (rem !== 0 || d.getSeconds() > 0) d.setMinutes(d.getMinutes() + (5 - rem), 0, 0);
  else d.setSeconds(0, 0);
  return d;
}

/** "DD/MM hh:mm AM" — the same 12-hour shape the receipt and the cards use. */
export function formatDraftDateTime(value: string | null | undefined): string {
  const d = parseDraftDate(value);
  const { hour12, minute, pm } = splitTwelveHour(d);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(hour12)}:${pad2(minute)} ${pm ? 'PM' : 'AM'}`;
}
